import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import * as Sentry from "@sentry/react";
import { hasCookieConsent, onCookieConsentChange } from "./lib/cookieConsent";
import { isPrivateCapabilityPath } from "./lib/sentryTelemetry";
import { isDemoMode } from "./lib/demo/demoMode";

// Private feedback and complaint-recovery links carry a one-use capability in
// the URL fragment. Do not initialise third-party monitoring on these pages so
// the token and the customer's private response stay between the customer,
// Dibs and the business receiving the feedback. Analytics cookie consent is
// also strictly required before Sentry initialises or sends events.
const isPrivateCapabilityPage = isPrivateCapabilityPath(window.location.pathname);
let sentryInitialized = false;

const syncSentryWithConsent = (analyticsConsented: boolean) => {
  if (!import.meta.env.VITE_SENTRY_DSN || isPrivateCapabilityPage) {
    return;
  }

  if (!analyticsConsented) {
    const client = Sentry.getClient();
    if (client) {
      client.getOptions().enabled = false;
    }
    return;
  }

  const existingClient = Sentry.getClient();
  if (sentryInitialized && existingClient) {
    existingClient.getOptions().enabled = true;
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE, // "production" | "development"
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    sendDefaultPii: false,
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,
    beforeSend(event, hint) {
      if (!hasCookieConsent("analytics")) {
        return null;
      }
      // Drop noisy browser extension / network errors
      const msg = hint.originalException instanceof Error ? hint.originalException.message : "";
      if (msg.includes("ResizeObserver loop") || msg.includes("Non-Error promise rejection captured")) {
        return null;
      }
      // Diagnostics must not become a second customer-data store. Strip user,
      // request and breadcrumb payloads; keep stack frames and low-cardinality
      // technical fields required to diagnose the release.
      event.user = undefined;
      event.request = undefined;
      event.extra = undefined;
      event.breadcrumbs = event.breadcrumbs?.map((breadcrumb) => ({
        ...breadcrumb,
        message: undefined,
        data: undefined,
      }));
      return event;
    },
  });
  sentryInitialized = true;
};

syncSentryWithConsent(hasCookieConsent("analytics"));
onCookieConsentChange((prefs) => {
  syncSentryWithConsent(prefs.analytics);
});

const mount = () => createRoot(document.getElementById("root")!).render(<App />);

if (isDemoMode()) {
  // The demo reads in-memory seed data, which is built on demand so ordinary
  // visitors never download it.
  import("./lib/demo/demoLoader").then(({ loadDemoData }) => loadDemoData()).then(mount);
} else {
  mount();
}
